import { useEffect, useId, useRef, useState } from "react";
import { Loader2, MapPin, Signpost } from "lucide-react";
import { fetchAddressSuggestions } from "@/lib/api";

// The backend (backend/addressauto/router.py MIN_QUERY_LENGTH) returns an empty list below 3
// characters without calling the provider, so asking earlier only wasted requests against the
// per-IP rate limit and cached misleading empty answers for "9" / "92". Keep the two in step.
const MIN_QUERY_LENGTH = 3;
// The server's rule (addressauto.config.meets_min_length) is the single source of truth: at least 3 non-space
// characters AND at least one letter, so a house number alone ("4922") never fires a request and never flashes
// "No suggestions" while the visitor is still typing the number.
export const meetsMinLength = (norm) => norm.replace(/\s/g, "").length >= MIN_QUERY_LENGTH && /\p{L}/u.test(norm);
// Trigger-side debounce. The dominant latency is the upstream provider (2-5s per request on the
// public Photon instance, profiled 2026-09-28 and 2026-10-14); this component hides it with a
// visible Searching state, a client-side prefix cache and progressive results, not by hammering
// the provider.
const DEBOUNCE_MS = 200;
// Never leave "Searching..." up forever (e.g. a cold-starting API): give up and show the manual-entry hint.
const REQUEST_TIMEOUT_MS = 10000;
// An empty answer may be a transient provider failure (the backend fails soft to an empty list), so
// empty results are only trusted briefly; non-empty results are kept for the session.
const EMPTY_TTL_MS = 60 * 1000;
const MAX_CACHE_ENTRIES = 60;

const normalize = (text) => (text || "").toLowerCase().replace(/\s+/g, " ").trim();
const wordsOf = (text) => text.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);

/**
 * True when every typed word is the start of some word in the suggestion's label, so "123 main"
 * keeps "123 South Main Street, Big Spring" and "9217 marvel dr dallas" keeps "9217 Marvel Drive,
 * Dallas". (The earlier plain startsWith on the street line dropped these and made the list vanish
 * mid-typing.)
 */
export function suggestionMatchesQuery(suggestion, query) {
  const typed = wordsOf(query);
  if (typed.length === 0) return true;
  const labelWords = wordsOf(suggestion.label || suggestion.line1 || "");
  const used = new Set();
  return typed.every((word) => {
    const idx = labelWords.findIndex((w, k) => !used.has(k) && w.startsWith(word));
    if (idx === -1) return false;
    used.add(idx);
    return true;
  });
}

/**
 * Street-address typeahead (Owner request 2026-09-24). A thin, accessible wrapper around a plain
 * text input -- a progressive enhancement over ordinary manual entry, never a requirement: the
 * backend (backend/addressauto) fails soft to an empty suggestion list on any upstream problem, and
 * this component never blocks typing, submitting, or clearing the field. The provider is only ever
 * reached through GET /api/address-autocomplete (lib/api.js), so a provider swap needs no UI change.
 *
 * Behavior (UI rebuilt 2026-10-14 after live repro: nothing showed for ~4.8s with no feedback):
 *   - Shows a "Searching..." row as soon as the visitor has typed enough, so a slow provider never
 *     looks like a dead field.
 *   - Session prefix cache: an exact repeat is served without a request; continuing to type past a
 *     cached query instantly filters the cached suggestions (word-prefix match) while the real
 *     fetch for the new text runs.
 *   - Cancellation: a request is aborted when the text diverges from it (backspace/edit), on
 *     selection, and on unmount. A request for an earlier part of what is being typed is NOT aborted
 *     on every keystroke (that starved slow providers for steady typists); it finishes, becomes the
 *     preview, and the latest text is fetched right after. A stale answer can never overwrite newer
 *     text or re-open the list after a choice.
 *   - No suggestions (or a failed/timed-out request): "No suggestions. Keep typing or enter your
 *     address manually." Manual entry and form submit are never affected.
 *   - Only the visitor's typing starts a search; a prefilled value (?address= handoff) does not
 *     pop the list open on page load.
 *
 * Follows the WAI-ARIA 1.2 combobox-with-listbox pattern (role="combobox" on the input,
 * role="listbox"/"option" on the popover, aria-activedescendant tracking the highlighted option),
 * plus a polite live region announcing searching / result count / no suggestions.
 *
 * Props:
 *   value, onChange(nextValue)   -- controlled input, same contract as a plain <input>.
 *   onSelectSuggestion(suggestion) -- called (in addition to onChange) when a suggestion is
 *     chosen: {label, line1, city, state, zip}. Callers use this to also fill a separate ZIP field.
 *     A suggestion may ALSO carry `match_type: "street_only"` and `house_number_confirmed: false`
 *     (backend/addressauto/photon.py's street-only fallback: when Photon's combined
 *     housenumber+street query returns nothing, a second query against the street name alone can
 *     still find a real street -- but never claims the typed house number exists on it). Those
 *     suggestions render with a distinct icon/caption so a shopper is never misled.
 *   className   -- passed to the <input> (defaults to this codebase's shared "input-brutal").
 *   inputTestId, listTestId -- data-testid hooks for tests (listTestId is the popover).
 *   Everything else (placeholder, id, required, inputMode, maxLength, autoComplete) passes through.
 */
export default function AddressAutocomplete({
  value, onChange, onSelectSuggestion, className = "input-brutal", inputTestId, listTestId,
  placeholder, id, required, ...rest
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  // "idle" (nothing to say) | "loading" (a fetch is running or about to) | "done" (answer is in).
  const [status, setStatus] = useState("idle");
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const timerRef = useRef(null);
  const inflightRef = useRef(null); // { norm, controller, timedOut }
  const latestRef = useRef({ raw: "", norm: "" }); // what the visitor currently has typed
  // Session cache: normalized query -> { results, at }. See lookup().
  const cacheRef = useRef(new Map());
  const listboxId = useId();
  const optionId = (i) => `${listboxId}-option-${i}`;

  const lookup = (norm) => {
    const now = Date.now();
    const cache = cacheRef.current;
    const hit = cache.get(norm);
    if (hit && (hit.results.length > 0 || now - hit.at < EMPTY_TTL_MS)) {
      return { results: hit.results, exact: true };
    }
    let bestKey = null;
    for (const [key, entry] of cache) {
      if (entry.results.length > 0 && norm.startsWith(key) && (bestKey === null || key.length > bestKey.length)) {
        bestKey = key;
      }
    }
    if (bestKey === null) return { results: [], exact: false };
    return { results: cache.get(bestKey).results.filter((s) => suggestionMatchesQuery(s, norm)), exact: false };
  };

  const remember = (norm, results) => {
    const cache = cacheRef.current;
    cache.delete(norm);
    cache.set(norm, { results, at: Date.now() });
    while (cache.size > MAX_CACHE_ENTRIES) cache.delete(cache.keys().next().value);
  };

  const cancelInflight = () => {
    const f = inflightRef.current;
    inflightRef.current = null;
    f?.controller.abort();
  };

  // Show whatever the cache can say about the text the visitor has now. Returns true when the
  // answer is final (exact cache hit), false when it is only a preview.
  const showLatest = () => {
    const { norm } = latestRef.current;
    const { results, exact } = lookup(norm);
    setSuggestions(results);
    setActiveIndex(-1);
    setStatus(exact ? "done" : "loading");
    return exact;
  };

  const startFetch = () => {
    timerRef.current = null;
    const { raw, norm } = latestRef.current;
    if (!meetsMinLength(norm) || lookup(norm).exact) return;
    const current = inflightRef.current;
    // A request for an earlier part of what is being typed is already on its way: let it finish
    // (its answer becomes the instant preview) instead of aborting it on every keystroke, which
    // starves a slow provider when the visitor types steadily. The latest text is fetched right after.
    if (current && norm.startsWith(current.norm)) return;
    cancelInflight();
    const controller = new AbortController();
    const flight = { norm, controller, timedOut: false };
    inflightRef.current = flight;
    const timeout = window.setTimeout(() => { flight.timedOut = true; controller.abort(); }, REQUEST_TIMEOUT_MS);
    const settle = (results, failed) => {
      window.clearTimeout(timeout);
      if (inflightRef.current !== flight) return; // cancelled or superseded: ignore the stale answer
      inflightRef.current = null;
      if (!failed) remember(norm, results);
      if (!meetsMinLength(latestRef.current.norm)) return;
      if (failed && latestRef.current.norm === norm) {
        setSuggestions([]);
        setActiveIndex(-1);
        setStatus("done");
        return;
      }
      if (showLatest()) return;
      // The text moved on while this was in flight: fetch the latest right away unless the
      // debounce timer is already about to.
      if (timerRef.current === null) startFetch();
    };
    fetchAddressSuggestions(raw, controller.signal)
      .then((results) => settle(Array.isArray(results) ? results : [], false))
      .catch(() => {
        // A non-critical enhancement: a failed/aborted/timed-out fetch just means no suggestions,
        // never an error shown next to an address field.
        window.clearTimeout(timeout);
        if (flight.timedOut || !controller.signal.aborted) settle([], true);
      });
  };

  // Driven by the visitor's typing only -- never by the `value` prop changing -- so a prefilled
  // address (the ?address= handoff) or a programmatic change does not pop the list open on load.
  const handleInput = (text) => {
    onChange(text);
    const norm = normalize(text);
    latestRef.current = { raw: text.trim().replace(/\s+/g, " "), norm };
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
    if (!meetsMinLength(norm)) {
      cancelInflight();
      setSuggestions([]);
      setActiveIndex(-1);
      setStatus("idle");
      setOpen(false);
      return;
    }
    setOpen(true);
    const current = inflightRef.current;
    if (current && !norm.startsWith(current.norm)) cancelInflight(); // diverged (backspace/edit): stale
    if (showLatest()) return;
    timerRef.current = window.setTimeout(startFetch, DEBOUNCE_MS);
  };

  useEffect(() => () => {
    window.clearTimeout(timerRef.current);
    inflightRef.current?.controller.abort();
    inflightRef.current = null;
  }, []);

  useEffect(() => {
    const onOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, []);

  const pick = (suggestion) => {
    // Cancel everything pending so a late answer can never re-open the list after a choice.
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
    cancelInflight();
    latestRef.current = { raw: "", norm: "" };
    setSuggestions([]);
    setStatus("idle");
    setOpen(false);
    setActiveIndex(-1);
    onChange(suggestion.line1);
    onSelectSuggestion?.(suggestion);
  };

  const hasList = open && suggestions.length > 0;
  const showStatusRow = open && status !== "idle" && (suggestions.length === 0 || status === "loading");

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      if (open) { e.preventDefault(); setOpen(false); setActiveIndex(-1); }
      return;
    }
    if ((e.key === "ArrowDown" || e.key === "ArrowUp") && !open && suggestions.length > 0) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!hasList) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      // Enter only picks a suggestion the visitor has highlighted; otherwise it submits the form
      // with exactly what they typed (manual entry always works).
      if (activeIndex >= 0) {
        e.preventDefault();
        pick(suggestions[activeIndex]);
      } else {
        setOpen(false);
      }
    }
  };

  // Keep the keyboard-highlighted option scrolled into view inside the (max-height) list.
  useEffect(() => {
    if (activeIndex < 0) return;
    document.getElementById(`${listboxId}-option-${activeIndex}`)?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, listboxId]);

  let liveMessage = "";
  if (open) {
    if (suggestions.length > 0) {
      liveMessage = `${suggestions.length} address suggestion${suggestions.length === 1 ? "" : "s"} available. Use the up and down arrow keys to review.`;
    } else if (status === "loading") {
      liveMessage = "Searching for addresses";
    } else if (status === "done") {
      liveMessage = "No suggestions. Keep typing or enter your address manually.";
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative"
      onBlur={(e) => { if (!containerRef.current?.contains(e.relatedTarget)) setOpen(false); }}
    >
      <input
        id={id}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={hasList}
        aria-controls={hasList ? listboxId : undefined}
        aria-activedescendant={hasList && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => {
          if (meetsMinLength(latestRef.current.norm) && status !== "idle") setOpen(true);
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        required={required}
        className={className}
        data-testid={inputTestId}
        {...rest}
      />
      <div role="status" aria-live="polite" className="sr-only" data-testid="address-autocomplete-live">{liveMessage}</div>
      {(hasList || showStatusRow) && (
        <div
          className="absolute z-30 mt-1.5 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
          data-testid={listTestId}
        >
          {hasList && (
            <ul
              id={listboxId}
              role="listbox"
              aria-label="Address suggestions"
              className="max-h-64 overflow-auto py-1.5"
            >
              {suggestions.map((s, i) => {
                // Street-only fallback match (backend/addressauto/photon.py): the street was confirmed
                // real, the typed house number was not -- rendered with a distinct icon/color and an
                // explicit caption so this is never visually confused with a confirmed address.
                const streetOnly = s.match_type === "street_only";
                // The backend already appends "(house number not verified)" to `label` for a caller that
                // only reads plain text (e.g. a screen reader) -- strip it from the visible primary line
                // since it's re-rendered below as its own styled caption.
                const primaryText = streetOnly
                  ? s.label.replace(/\s*\(house number not verified\)\s*$/, "")
                  : s.label;
                return (
                  <li
                    key={s.label}
                    role="option"
                    id={optionId(i)}
                    aria-selected={i === activeIndex}
                    aria-label={s.label}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(s)}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`w-full text-left px-3.5 py-3 flex items-start gap-2.5 cursor-pointer transition-colors ${
                      i === activeIndex ? "bg-emerald-50" : "hover:bg-slate-50"
                    }`}
                    data-testid="address-suggestion-option"
                  >
                    {streetOnly ? (
                      <Signpost className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" aria-hidden="true" />
                    ) : (
                      <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" aria-hidden="true" />
                    )}
                    <span className="flex flex-col">
                      <span className="text-sm text-[#0A2540]">{primaryText}</span>
                      {streetOnly && (
                        <span className="text-xs text-amber-600">House number not verified -- street match only</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {showStatusRow && (
            <div
              className={`px-3.5 py-2.5 text-slate-500 flex items-center gap-2 ${hasList ? "border-t border-slate-100 text-xs" : "text-sm"}`}
              aria-hidden="true"
              data-testid="address-autocomplete-status"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none shrink-0" aria-hidden="true" />
                  <span>{hasList ? "Updating suggestions..." : "Searching..."}</span>
                </>
              ) : (
                <span>No suggestions. Keep typing or enter your address manually.</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
